import React, { useState } from "react";
import { Controller, Control, FieldErrors, UseFormSetValue, UseFormTrigger, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setAccountType } from "../../../../../stores/slices/sign-up-slice";
import { useLazyVerifyReferralCodeQuery } from "../../../../../service/customer-service-v2";
import { CircleInfoIcon, CorporateTabIcon, CorporateTabIconActive, IndividualTabIcon, IndividualTabIconActive, InfoIcon } from "../../../../../assets/icons";
import styles from "./multi-step-form.module.scss";
import { FormData } from "./types";
import QuickGuide from "../../../../../common/ekedc-components/quick-guide/QuickGuide";

interface Props {
	control: Control<FormData>;
	errors: FieldErrors<FormData>;
	accountType: "Corporate" | "Individual";
	setValue: UseFormSetValue<FormData>;
	trigger: UseFormTrigger<FormData>;
	onNext: () => void;
}

export const Step1PersonalInfo: React.FC<Props> = ({ control, errors, accountType, setValue, trigger, onNext }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [referralCodeError, setReferralCodeError] = useState<string | null>(null);
	const referralCode = useWatch({ control, name: "referralCode" });
	const [verifyReferralCode, { isLoading: isVerifyingReferralCode }] = useLazyVerifyReferralCodeQuery();

	const handleNext = async () => {
		const fields: (keyof FormData)[] = ["firstName", "lastName"];
		if (accountType === "Corporate") fields.push("organizationName");
		if (!(await trigger(fields))) return;

		if (referralCode && referralCode.trim()) {
			const result = await verifyReferralCode(referralCode.trim());
			if (result.data !== true) {
				setReferralCodeError(t("auth.web.referralCodeIncorrect"));
				return;
			}
		}
		setReferralCodeError(null);
		onNext();
	};

	return (
		<div className={styles.stepContent}>
			<div className={styles.card}>
				<div className={styles.cardHeader}>
					<span className={styles.cardLabel}>{t("common.labels.selectAccountType")}</span>
					<QuickGuide icon={<CircleInfoIcon />} title={t("common.labels.signinHelpTitle")} description={t("common.labels.signinHelpDescription")} />
				
				</div>
				<div className={styles.accountTypeTabs}>
					<button
						type="button"
						className={`${styles.tab} ${accountType === "Corporate" ? styles.activeTab : ""}`}
						onClick={() => {
							setValue("accountType", "Corporate");
							dispatch(setAccountType("Corporate"));
						}}
					>
						<span className={styles.tabIcon}>{accountType === "Corporate" ? <CorporateTabIconActive /> : <CorporateTabIcon />}</span>
						<span className={styles.tabLabel}>{t("auth.corporate")}</span>
					</button>
					<button
						type="button"
						className={`${styles.tab} ${accountType === "Individual" ? styles.activeTab : ""}`}
						onClick={() => {
							setValue("accountType", "Individual");
							dispatch(setAccountType("Individual"));
						}}
					>
						<span className={styles.tabIcon}>{accountType === "Individual" ? <IndividualTabIconActive /> : <IndividualTabIcon />}</span>
						<span className={styles.tabLabel}>{t("auth.individual")}</span>
					</button>
				</div>
			</div>

			{accountType === "Corporate" && (
				<div className={styles.card}>
					<div className={styles.cardHeader}>
						<span className={styles.cardLabel}>{t("account.corporateName")}</span>
						<QuickGuide icon={<CircleInfoIcon />} title={t("common.labels.signinHelpTitle")} description={t("common.labels.signinHelpDescription")} />
				
					</div>
					<Controller
						name="organizationName"
						control={control}
						rules={{ required: t("auth.organizationNameRequired") }}
						render={({ field }) => (
							<>
								<input
									{...field}
									placeholder={t("auth.web.organizationNamePlaceholder")}
									className={`${styles.cardInput} ${errors.organizationName ? styles.inputError : ""}`}
								/>
								{errors.organizationName && <span className={styles.fieldError}>{errors.organizationName.message}</span>}
							</>
						)}
					/>
				</div>
			)}

			<div className={`${styles.card} ${styles.felxGapSection}`}>
				<div className={styles.cardHeader}>
					<span className={styles.cardLabel}>{accountType === "Corporate" ? t("auth.contactPerson") : t("auth.personalInformation")}</span>
						<QuickGuide icon={<CircleInfoIcon />} title={t("common.labels.signinHelpTitle")} description={t("common.labels.signinHelpDescription")} />
					</div>
				<Controller
					name="firstName"
					control={control}
					rules={{
						required: t("common.validation.firstNameRequired"),
						minLength: { value: 2, message: t("auth.web.mustBeAtLeastTwoCharacters") },
						pattern: { value: /^[a-zA-Z\s'-]+$/, message: t("auth.web.firstNameLettersOnly") },
					}}
					render={({ field }) => (
						<div className={styles.cardField}>
							<span className={styles.fieldLabel}>{t("common.labels.firstName")}</span>
							<input {...field} placeholder={t("common.labels.firstNamePlaceholder")} className={`${styles.cardInput} ${errors.firstName ? styles.inputError : ""}`} />
							{errors.firstName && <span className={styles.fieldError}>{errors.firstName.message}</span>}
						</div>
					)}
				/>
				<Controller
					name="lastName"
					control={control}
					rules={{
						required: t("auth.web.surnameRequired"),
						minLength: { value: 2, message: t("auth.web.mustBeAtLeastTwoCharacters") },
						pattern: { value: /^[a-zA-Z\s'-]+$/, message: t("auth.web.surnameLettersOnly") },
					}}
					render={({ field }) => (
						<div className={styles.cardField}>
							<span className={styles.fieldLabel}>{t("common.labels.surname")}</span>
							<input {...field} placeholder={t("common.labels.surnamePlaceholder")} className={`${styles.cardInput} ${errors.lastName ? styles.inputError : ""}`} />
							{errors.lastName && <span className={styles.fieldError}>{errors.lastName.message}</span>}
						</div>
					)}
				/>
				<Controller
					name="referralCode"
					control={control}
					render={({ field }) => (
						<div className={styles.cardField}>
							<span className={styles.fieldLabel}>{t("auth.referralCodeOptional")}</span>
							<input
								{...field}
								placeholder={t("auth.referralCodePlaceholder")}
								className={`${styles.cardInput} ${referralCodeError ? styles.inputError : ""}`}
								onChange={e => {
									field.onChange(e);
									setReferralCodeError(null);
								}}
							/>
							{referralCodeError && <span className={styles.fieldError}>{referralCodeError}</span>}
						</div>
					)}
				/>
			</div>
			
			<div className={styles.buttonSection}>
				<p className={styles.termsText}>
					{t("auth.byContinuingYouAgree")}
					<a href="/terms" className={styles.termsLink}>
						{t("account.termsOfService")}
					</a>
					{t("auth.andUnderstoodPrivacyPolicy")}
					<a href="/privacy-policy" className={styles.termsLink}>
						{t("account.privacyPolicy")}
					</a>
				</p>

				<button type="button" className={styles.continueButton} onClick={handleNext} disabled={isVerifyingReferralCode}>
					{isVerifyingReferralCode ? t("auth.web.verifying") : t("common.actions.continueKey")}
				</button>
			</div>
		</div>
	);
};
